import { List, useTable } from '@refinedev/antd';
import type { CrudFilters } from '@refinedev/core';
import { Table, Form, DatePicker, Select, Button, Space, Tooltip, Empty } from 'antd';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { DecisionTag } from '../audit-log/decision-tag';
import { ActionModal } from './action-modal';

interface QueueRow {
  id: string;
  timestamp: string;
  action: string;
  filter_decision: string | null;
  user_id: string | null;
  community_id: string;
  organization_id: string | null;
}

interface SearchForm {
  dateRange?: [unknown, unknown];
  filter_decision?: string;
}

const BLOCK_DECISION_KEYS = ['block_pre_filter', 'block_post_filter'] as const;

export function ModerationQueueList({ scope }: { scope: 'community' | 'organization' }) {
  const { t } = useTranslation();
  const { communityId, organizationId } = useParams();
  const parentId = scope === 'community' ? communityId : organizationId;
  const [modalRecord, setModalRecord] = useState<QueueRow | null>(null);

  const decisionOptions = BLOCK_DECISION_KEYS.map((k) => ({
    value: k,
    label: t(`auditLog.decision.${k}`),
  }));

  const { tableProps, searchFormProps } = useTable<QueueRow>({
    resource: 'moderation-queue',
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: 'timestamp', order: 'desc' }] },
    meta: { parentId, scope },
    syncWithLocation: true,
    onSearch: (data): CrudFilters => {
      const { dateRange, filter_decision } = data as SearchForm;
      const filters: CrudFilters = [];
      if (dateRange?.[0]) {
        filters.push({
          field: 'timestamp_from',
          operator: 'eq',
          value: (dateRange[0] as { toISOString: () => string }).toISOString(),
        });
      }
      if (dateRange?.[1]) {
        filters.push({
          field: 'timestamp_to',
          operator: 'eq',
          value: (dateRange[1] as { toISOString: () => string }).toISOString(),
        });
      }
      if (filter_decision) {
        filters.push({ field: 'filter_decision', operator: 'eq', value: filter_decision });
      }
      return filters;
    },
  });

  return (
    <List title={t('moderationQueue.title')}>
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="dateRange" label={t('moderationQueue.filters.timestamp')}>
          <DatePicker.RangePicker showTime />
        </Form.Item>
        <Form.Item name="filter_decision" label={t('moderationQueue.filters.filterDecision')}>
          <Select
            allowClear
            placeholder={t('common.search')}
            options={decisionOptions}
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              {t('common.search')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <Table
        {...tableProps}
        rowKey="id"
        locale={{ emptyText: <Empty description={t('moderationQueue.empty')} /> }}
      >
        <Table.Column<QueueRow>
          dataIndex="timestamp"
          title={t('moderationQueue.columns.timestamp')}
          render={(v) => formatDateBidi(v)}
        />
        <Table.Column<QueueRow>
          dataIndex="action"
          title={t('moderationQueue.columns.action')}
          render={(v) => <BidiText>{v}</BidiText>}
        />
        <Table.Column<QueueRow>
          dataIndex="filter_decision"
          title={t('moderationQueue.columns.filterDecision')}
          render={(v) => <DecisionTag value={v} />}
        />
        <Table.Column<QueueRow>
          dataIndex="user_id"
          title={t('moderationQueue.columns.userId')}
          render={(v: string | null) =>
            v ? (
              <Tooltip title={v}>
                <MonoCode>{v.slice(0, 8)}…</MonoCode>
              </Tooltip>
            ) : '—'
          }
        />
        {scope === 'community' && (
          <Table.Column<QueueRow>
            dataIndex="organization_id"
            title={t('moderationQueue.columns.organization')}
            render={(v: string | null) =>
              v ? (
                <Tooltip title={v}>
                  <MonoCode>{v.slice(0, 8)}…</MonoCode>
                </Tooltip>
              ) : '—'
            }
          />
        )}
        <Table.Column<QueueRow>
          title={t('common.actions')}
          render={(_, record) => (
            <Button type="primary" size="small" onClick={() => setModalRecord(record)}>
              {t('moderationQueue.takeAction')}
            </Button>
          )}
        />
      </Table>
      <ActionModal
        record={modalRecord}
        open={!!modalRecord}
        onClose={() => setModalRecord(null)}
      />
    </List>
  );
}
